import { useEffect, useState } from "react";
import { Megaphone, Plus, Trash2, Save, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";

const empty = {
  message: "",
  cta_text: "",
  cta_url: "",
  background: "#FF6B00",
  text_color: "#FFFFFF",
  active: true,
  display_order: 0,
  marquee: false,
};

export default function Offers() {
  usePageTitle("Offer Strip");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setRows((await api.get("/admin/offers")).data); }
    catch { toast.error("Could not load offers"); }
  };
  useEffect(() => { load(); }, []);

  const clean = (o) => {
    const p = { ...o };
    delete p.id; delete p.created_at; delete p._id;
    return p;
  };

  const save = async () => {
    if (!selected.message?.trim()) { toast.error("Message is required"); return; }
    setSaving(true);
    try {
      const payload = clean(selected);
      if (selected.id) {
        await api.put(`/admin/offers/${selected.id}`, payload);
        toast.success("Offer updated");
      } else {
        await api.post("/admin/offers", payload);
        toast.success("Offer created");
      }
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (o) => {
    if (!window.confirm("Delete this offer?")) return;
    try { await api.delete(`/admin/offers/${o.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const toggleActive = async (o) => {
    try {
      await api.put(`/admin/offers/${o.id}`, { ...clean(o), active: !o.active });
      load();
    } catch { toast.error("Failed"); }
  };

  const move = async (o, delta) => {
    try {
      await api.put(`/admin/offers/${o.id}`, { ...clean(o), display_order: (o.display_order || 0) + delta });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Megaphone className="text-[#FF6B00]" />
          <h1 className="text-2xl font-black tracking-tight">Offer Strip (Above Header)</h1>
        </div>
        <button onClick={() => setSelected({ ...empty, display_order: rows.length })} className="btn-swiss btn-accent text-xs" data-testid="offer-new-btn">
          <Plus size={12} /> New Offer
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {rows.map((o) => (
          <div key={o.id} className="border-2 border-zinc-200 rounded-2xl bg-white overflow-hidden flex flex-col md:flex-row" data-testid={`offer-row-${o.id}`}>
            {/* Live preview strip */}
            <div className="md:w-1/2 p-3 flex items-center justify-center text-sm font-medium" style={{ background: o.background || "#FF6B00", color: o.text_color || "#FFFFFF" }}>
              <span className="truncate">{o.message}</span>
              {o.cta_text && <span className="ml-2 underline font-bold text-xs">{o.cta_text} →</span>}
            </div>
            <div className="flex-1 p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
              <div className="flex-1">
                <div className="text-xs text-zinc-500">
                  Order: {o.display_order || 0} · BG: {o.background} · Text: {o.text_color}
                </div>
                {o.cta_url && <div className="text-[10px] text-zinc-500 mt-1">Link: {o.cta_url}</div>}
              </div>
              <div className="flex flex-col md:flex-row gap-1 items-start md:items-center">
                <button onClick={() => move(o, -1)} className="p-1.5 text-zinc-500 hover:text-[#FF6B00]" title="Move up"><ArrowUp size={14} /></button>
                <button onClick={() => move(o, 1)} className="p-1.5 text-zinc-500 hover:text-[#FF6B00]" title="Move down"><ArrowDown size={14} /></button>
                <button onClick={() => toggleActive(o)} className={`p-1.5 ${o.active ? "text-emerald-600" : "text-zinc-400"}`} title={o.active ? "Active" : "Hidden"} data-testid={`offer-toggle-${o.id}`}>
                  {o.active ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button onClick={() => setSelected({ ...o })} className="text-xs font-bold text-[#FF6B00]" data-testid={`offer-edit-${o.id}`}>Edit</button>
                <button onClick={() => remove(o)} className="text-red-500 hover:text-red-700 p-1.5" data-testid={`offer-del-${o.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="p-10 border-2 border-dashed border-zinc-200 rounded-2xl text-center text-sm text-zinc-500">
            No offer strips yet. Click "New Offer" to add one above the site header.
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full max-w-xl bg-white h-full overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="offer-editor">
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-4 border-b border-zinc-100 z-10">
              <div>
                <div className="overline text-[#FF6B00]">{selected.id ? "Edit Offer" : "New Offer"}</div>
                <div className="font-display text-2xl font-black tracking-tight">Offer Strip</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setSelected(null)} className="btn-swiss btn-outline text-xs">Cancel</button>
                <button onClick={save} disabled={saving} className="btn-swiss btn-accent text-xs" data-testid="offer-save"><Save size={12} /> Save</button>
              </div>
            </div>

            {/* Live preview */}
            <div className="mb-5 p-3 rounded-xl text-sm font-medium overflow-hidden" style={{ background: selected.background || "#FF6B00", color: selected.text_color || "#FFFFFF" }}>
              {selected.marquee ? (
                <div className="whitespace-nowrap animate-marquee inline-block">
                  {selected.message || "Your offer message preview…"}
                  {selected.cta_text && <span className="ml-2 underline font-bold text-xs">{selected.cta_text} →</span>}
                  <span className="mx-12 opacity-60">•</span>
                  {selected.message || "Your offer message preview…"}
                </div>
              ) : (
                <div className="text-center">
                  {selected.message || "Your offer message preview…"}
                  {selected.cta_text && <span className="ml-2 underline font-bold text-xs">{selected.cta_text} →</span>}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <Field label="Message*">
                <input className="input" value={selected.message} onChange={(e) => setSelected({ ...selected, message: e.target.value })} placeholder="Flat 20% off on all Business Cards this week" data-testid="offer-msg-input" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="CTA Text (optional)">
                  <input className="input" value={selected.cta_text || ""} onChange={(e) => setSelected({ ...selected, cta_text: e.target.value })} placeholder="Shop Now" data-testid="offer-cta-text-input" />
                </Field>
                <Field label="CTA URL">
                  <input className="input" value={selected.cta_url || ""} onChange={(e) => setSelected({ ...selected, cta_url: e.target.value })} placeholder="/products" data-testid="offer-cta-url-input" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Background Colour">
                  <div className="flex items-center gap-2">
                    <input type="color" className="h-10 w-14 border border-zinc-200 rounded-md cursor-pointer" value={selected.background || "#FF6B00"} onChange={(e) => setSelected({ ...selected, background: e.target.value })} />
                    <input className="input flex-1 font-mono" value={selected.background || ""} onChange={(e) => setSelected({ ...selected, background: e.target.value })} />
                  </div>
                </Field>
                <Field label="Text Colour">
                  <div className="flex items-center gap-2">
                    <input type="color" className="h-10 w-14 border border-zinc-200 rounded-md cursor-pointer" value={selected.text_color || "#FFFFFF"} onChange={(e) => setSelected({ ...selected, text_color: e.target.value })} />
                    <input className="input flex-1 font-mono" value={selected.text_color || ""} onChange={(e) => setSelected({ ...selected, text_color: e.target.value })} />
                  </div>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Display Order">
                  <input type="number" className="input" value={selected.display_order || 0} onChange={(e) => setSelected({ ...selected, display_order: parseInt(e.target.value) || 0 })} />
                </Field>
                <label className="flex items-center gap-2 pt-6">
                  <input type="checkbox" checked={!!selected.active} onChange={(e) => setSelected({ ...selected, active: e.target.checked })} data-testid="offer-active-toggle" /> Active
                </label>
              </div>
              <label className="flex items-start gap-2 p-3 rounded-xl border-2 border-orange-100 bg-orange-50/40 cursor-pointer hover:border-[#FF6B00]" data-testid="offer-marquee-label">
                <input
                  type="checkbox"
                  checked={!!selected.marquee}
                  onChange={(e) => setSelected({ ...selected, marquee: e.target.checked })}
                  className="mt-0.5 w-4 h-4 accent-[#FF6B00]"
                  data-testid="offer-marquee-toggle"
                />
                <div>
                  <div className="text-sm font-bold text-zinc-800">Running text (marquee)</div>
                  <div className="text-[11px] text-zinc-500">Scrolls the message right → left across the strip like a news ticker. Best for longer messages.</div>
                </div>
              </label>
              <div className="text-[10px] text-zinc-500">💡 If multiple offers are active, they will rotate automatically every 5 seconds above the header.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
